import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";

import { supabase } from "@/integrations/supabase/client";

type AuthorizationDetails = {
  client?: { name?: string; redirect_uri?: string; scope?: string } | null;
  redirect_url?: string | null;
  redirect_to?: string | null;
  scope?: string | null;
};

type OAuthNamespace = {
  getAuthorizationDetails: (
    id: string,
  ) => Promise<{ data: AuthorizationDetails | null; error: Error | null }>;
  approveAuthorization: (
    id: string,
  ) => Promise<{
    data: { redirect_url?: string | null; redirect_to?: string | null } | null;
    error: Error | null;
  }>;
  denyAuthorization: (
    id: string,
  ) => Promise<{
    data: { redirect_url?: string | null; redirect_to?: string | null } | null;
    error: Error | null;
  }>;
};

// The `auth.oauth` namespace is beta; type it locally to avoid depending on SDK internals.
function oauth(): OAuthNamespace {
  return (supabase.auth as unknown as { oauth: OAuthNamespace }).oauth;
}

export const Route = createFileRoute("/.lovable/oauth/consent")({
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id: typeof s.authorization_id === "string" ? s.authorization_id : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
    const { data } = await supabase.auth.getSession();
    const next = location.pathname + location.searchStr;
    if (!data.session) throw redirect({ to: "/auth", search: { next } });
  },
  loader: async ({ location }) => {
    const id = new URLSearchParams(location.search).get("authorization_id")!;
    const { data, error } = await oauth().getAuthorizationDetails(id);
    if (error) throw error;
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate });
    return data;
  },
  component: Consent,
  errorComponent: ({ error }) => (
    <main className="container-prose py-20 text-foreground">
      <h1 className="font-serif text-3xl">Authorization error</h1>
      <p className="mt-3 text-muted-foreground">
        {(error as Error)?.message ?? String(error)}
      </p>
    </main>
  ),
});

function Consent() {
  const details = Route.useLoaderData();
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientName = details?.client?.name ?? "an app";
  const scopeString = details?.client?.scope ?? details?.scope ?? "openid email profile";

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const result = approve
      ? await oauth().approveAuthorization(authorization_id)
      : await oauth().denyAuthorization(authorization_id);
    if (result.error) {
      setBusy(false);
      setError(result.error.message);
      return;
    }
    const target = result.data?.redirect_url ?? result.data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  return (
    <main className="mx-auto max-w-lg px-6 py-24 text-foreground">
      <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
        Authorize connection
      </p>
      <h1 className="mt-6 font-serif text-4xl">
        Connect <span className="italic text-[color:var(--color-leaf)]">{clientName}</span> to
        your garden
      </h1>
      <p className="mt-6 text-muted-foreground text-pretty">
        {clientName} will be able to call this site's MCP tools while you are signed in — read
        drafts, list essays, and any other tools the server exposes.
      </p>
      <ul className="mt-8 space-y-2 border-y hairline py-6 text-sm">
        <li>
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
            Requesting
          </span>{" "}
          {scopeString.split(/\s+/).join(" · ")}
        </li>
        <li className="text-muted-foreground">
          This does not bypass any of the site's own permissions.
        </li>
      </ul>
      {error && (
        <p role="alert" className="mt-6 text-sm text-red-700">
          {error}
        </p>
      )}
      <div className="mt-10 flex gap-3">
        <button
          onClick={() => decide(true)}
          disabled={busy}
          className="inline-flex items-center justify-center rounded-full bg-foreground px-6 py-2.5 text-sm text-background transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          Approve
        </button>
        <button
          onClick={() => decide(false)}
          disabled={busy}
          className="inline-flex items-center justify-center rounded-full border hairline px-6 py-2.5 text-sm text-foreground transition-colors hover:bg-secondary/60 disabled:opacity-50"
        >
          Deny
        </button>
      </div>
    </main>
  );
}
